import { useState, useEffect } from 'react'
import { useMsal } from '@azure/msal-react'
import { useTheme } from '../context/ThemeContext'
import { apiConfig, getApiToken } from '../auth/authConfig'

// Quarterly update decks, exactly as emailed to investors. The PDFs sit behind the
// API's investor check (api/decks); opening one fetches a 5-minute signed link.

const formatSent = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatSize = (bytes) =>
    bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`

function BoardDecks() {
    const { theme } = useTheme()
    const isDark = theme === 'dark'
    const { instance } = useMsal()

    const [decks, setDecks] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [opening, setOpening] = useState(null)
    const [openError, setOpenError] = useState(null)

    const authHeaders = async () => {
        const token = await getApiToken(instance)
        return token ? { 'X-Aviator-ID-Token': token } : {}
    }

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const res = await fetch(`${apiConfig.baseUrl}/decks`, { headers: await authHeaders() })
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}))
                    throw new Error(body.detail || 'Failed to load board decks')
                }
                const json = await res.json()
                if (!cancelled) setDecks(json.decks || [])
            } catch (err) {
                if (!cancelled) setError(err.message)
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => { cancelled = true }
    }, [])

    const openDeck = async (deck) => {
        // Open the tab now, inside the click, so popup blockers allow it; point it
        // at the PDF once the signed link comes back.
        const win = window.open('', '_blank')
        if (win) {
            win.opener = null
            win.document.title = 'Opening deck...'
            win.document.body.textContent = 'Opening deck...'
        }
        setOpening(deck.id)
        setOpenError(null)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/decks/${encodeURIComponent(deck.id)}/link`, { headers: await authHeaders() })
            const body = await res.json().catch(() => ({}))
            if (!res.ok || !body.url) throw new Error(body.detail || 'Could not open the deck')
            const url = new URL(body.url, window.location.origin).href
            if (win) win.location.href = url
            else window.location.href = url
        } catch (err) {
            if (win) win.close()
            setOpenError(err.message)
        } finally {
            setOpening(null)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div>
                <h1 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Board Decks
                </h1>
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Quarterly investor updates, as sent to investors.
                </p>
            </div>

            {error && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">{error}</div>
            )}
            {openError && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">{openError}</div>
            )}

            {decks && decks.length === 0 && !error && (
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No decks have been posted yet.</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {(decks || []).map(deck => (
                    <div key={deck.id} className="kpi-card !p-4 sm:!p-6 flex flex-col">
                        <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-10 h-12 rounded flex items-center justify-center text-[10px] font-bold ${
                                isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700'
                            }`}>
                                PDF
                            </div>
                            <div className="min-w-0">
                                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{deck.title}</p>
                                <p className="kpi-card-subtitle !mt-0.5">Sent {formatSent(deck.sent)}</p>
                                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                    {deck.pages} pages &middot; {formatSize(deck.sizeBytes)}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => openDeck(deck)}
                            disabled={opening === deck.id}
                            className="mt-4 self-start px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
                        >
                            {opening === deck.id ? 'Opening...' : 'Open'}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default BoardDecks
